<div class="p-4">
  <div class="mb-6">
    <h1 class="text-2xl font-semibold">Licenses</h1>
    <p class="mt-1 text-sm text-base-content/70">
      Query desktop licenses by expiration date and product. Empty product
      selection queries all products.
    </p>
  </div>

  <div class="mb-6 flex flex-wrap gap-4">
    <label class="form-control">
      <div class="label">
        <span class="label-text">Expiration from</span>
      </div>
      <input type="date" class="input input-bordered" [(ngModel)]="fromDate" />
    </label>
    <label class="form-control">
      <div class="label">
        <span class="label-text">Expiration to</span>
      </div>
      <input type="date" class="input input-bordered" [(ngModel)]="toDate" />
    </label>
    <div class="form-control min-w-80 flex-1">
      <div class="label">
        <span class="label-text">Products</span>
      </div>
      @if (loadingProducts) {
        <span class="loading loading-spinner loading-sm"></span>
      } @else if (errorFetchingProducts) {
        <p class="text-error">{{ errorFetchingProducts }}</p>
      } @else {
        <div
          #productDropdown
          class="dropdown w-full"
          [class.dropdown-open]="productDropdownOpen"
        >
          <div
            tabindex="0"
            role="button"
            class="btn w-full justify-between border-base-content/20 bg-base-100"
            (click)="toggleProductDropdown(); $event.stopPropagation()"
          >
            <span class="truncate">{{ productDropdownLabel }}</span>
            <app-chevron-down-icon
              class="h-5 w-5 shrink-0"
            ></app-chevron-down-icon>
          </div>
          <ul
            tabindex="0"
            class="menu dropdown-content z-[1] max-h-96 w-full flex-nowrap overflow-y-auto rounded-box border border-base-content/20 bg-base-100 p-2 shadow"
            (click)="$event.stopPropagation()"
          >
            <li>
              <input
                #productSearchInput
                type="text"
                class="input input-bordered w-full"
                placeholder="Search Products..."
                [(ngModel)]="productSearch"
                (click)="$event.stopPropagation()"
              />
            </li>
            <li class="flex flex-row gap-2 px-1 py-1">
              <button
                type="button"
                class="btn btn-ghost btn-xs"
                [disabled]="selectedProductIds.size === 0"
                (click)="clearSelectedProducts(); $event.stopPropagation()"
              >
                Clear
              </button>
            </li>
            @for (
              product of filteredProducts;
              track product.id;
              let i = $index
            ) {
              <li
                [ngClass]="{
                  'border-t border-base-content/20': i > 0,
                }"
              >
                <label class="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-sm"
                    [checked]="isProductSelected(product.id)"
                    (change)="toggleProduct(product.id)"
                    (click)="$event.stopPropagation()"
                  />
                  <p class="flex flex-wrap gap-1">
                    <span>{{ product.code }}</span>
                    <span>-</span>
                    <span>
                      {{ product.title }} ({{ product.variance_code }})
                    </span>
                  </p>
                </label>
              </li>
            } @empty {
              <li class="disabled">
                <span>No products match</span>
              </li>
            }
          </ul>
        </div>
      }
    </div>
    <div class="flex items-end gap-2">
      <button
        class="btn btn-primary"
        [disabled]="!canQuery || querying"
        (click)="queryLicenses()"
      >
        @if (querying) {
          <span class="loading loading-spinner loading-sm"></span>
        }
        Search
      </button>
      <button
        class="btn"
        [disabled]="licenses.length === 0"
        (click)="exportCsv()"
      >
        Export CSV
      </button>
    </div>
  </div>

  @if (errorQuerying) {
    <p class="mb-4 text-error">{{ errorQuerying }}</p>
  }

  @if (queried && !querying) {
    <p class="mb-2 text-sm text-base-content/70">
      {{ licenses.length }} license{{ licenses.length === 1 ? '' : 's' }}
    </p>
    <div class="overflow-x-auto rounded-lg border border-base-content/20">
      <table
        class="table table-pin-rows"
        [ngClass]="{
          'table-lg': tableSpacing === 'comfortable',
          'table-md': tableSpacing === 'normal',
          'table-xs': tableSpacing === 'compact',
        }"
      >
        <thead class="text-base font-medium">
          <tr>
            <th>
              <button
                type="button"
                class="link-hover link inline-flex items-center gap-1"
                (click)="sortBy('account')"
              >
                Account
                @if (sortField === 'account') {
                  @if (sortDirection === 'ASC') {
                    <app-chevron-up-icon class="inline-block h-4 w-4" />
                  } @else {
                    <app-chevron-down-icon class="inline-block h-4 w-4" />
                  }
                }
              </button>
            </th>
            <th>
              <button
                type="button"
                class="link-hover link inline-flex items-center gap-1"
                (click)="sortBy('company')"
              >
                Company
                @if (sortField === 'company') {
                  @if (sortDirection === 'ASC') {
                    <app-chevron-up-icon class="inline-block h-4 w-4" />
                  } @else {
                    <app-chevron-down-icon class="inline-block h-4 w-4" />
                  }
                }
              </button>
            </th>
            <th>Product</th>
            <th>
              <button
                type="button"
                class="link-hover link inline-flex items-center gap-1"
                (click)="sortBy('expiration')"
              >
                Expiration
                @if (sortField === 'expiration') {
                  @if (sortDirection === 'ASC') {
                    <app-chevron-up-icon class="inline-block h-4 w-4" />
                  } @else {
                    <app-chevron-down-icon class="inline-block h-4 w-4" />
                  }
                }
              </button>
            </th>
            <th>Active</th>
            <th>Will renew</th>
            <th>Site</th>
          </tr>
        </thead>
        <tbody>
          @if (licenses.length === 0) {
            <tr>
              <td colspan="10">No licenses found</td>
            </tr>
          } @else {
            @for (license of pagedLicenses; track license.id) {
              <tr>
                <td>
                  <a
                    class="link"
                    (click)="
                      goToTenant(license.tenant.id); $event.preventDefault()
                    "
                    href="/admin/tenant/{{ license.tenant.id }}"
                  >
                    {{ license.tenant.account_number }}
                  </a>
                </td>
                <td>
                  <a
                    class="link"
                    (click)="
                      goToTenant(license.tenant.id); $event.preventDefault()
                    "
                    href="/admin/tenant/{{ license.tenant.id }}"
                  >
                    {{ license.tenant.company }}
                  </a>
                </td>
                <td>
                  <a
                    class="link"
                    (click)="goToLicense(license.id); $event.preventDefault()"
                    href="/admin/edit-license/{{ license.id }}"
                  >
                    {{ license.product.code }} - {{ license.product.title }}
                  </a>
                </td>
                <td>{{ formatDate(license.expiration) }}</td>
                <td>{{ license.active ? 'Yes' : 'No' }}</td>
                <td>{{ license.will_renew ? 'Yes' : 'No' }}</td>
                <td>{{ license.site_license ? 'Yes' : 'No' }}</td>
              </tr>
            }
          }
        </tbody>
      </table>
    </div>
    @if (licenses.length > 0) {
      <div class="mt-4">
        <app-pagination
          [spacing]="tableSpacing"
          [pageSize]="pageSize"
          [currentPage]="currentPage"
          [totalPages]="totalPages"
          (pageChanged)="currentPage = $event"
          (settingsChanged)="handleSettingsChanged($event)"
        ></app-pagination>
      </div>
    }
  }
</div>
